import { useState } from "react";
import { hotComments } from "@/mocks/evidence";

const badgeStyle: Record<string, string> = {
  置顶: "bg-accent-500 text-foreground-950",
  热门: "bg-primary-500 text-background-50",
  热: "bg-secondary-300 text-secondary-950",
  新: "bg-background-200 text-foreground-600",
};

const avatarColors = [
  "bg-primary-200 text-primary-900",
  "bg-accent-200 text-accent-900",
  "bg-secondary-300 text-secondary-950",
  "bg-primary-100 text-primary-800",
  "bg-accent-100 text-accent-800",
];

export default function Comments() {
  const [agreed, setAgreed] = useState<string[]>([]);
  const [extra, setExtra] = useState<{ name: string; text: string } | null>(null);
  const [draft, setDraft] = useState("");
  const toggle = (id: string) =>
    setAgreed((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submit = () => {
    if (!draft.trim()) return;
    setExtra({ name: "曝瓜网友", text: draft.trim() });
    setDraft("");
  };

  return (
    <section id="comments" className="mx-auto max-w-[1400px] scroll-mt-24 px-4 py-10 md:px-6 md:py-12">
      <div className="grid gap-6 lg:grid-cols-12">
        {/* 讨论引导卡（醒目） */}
        <div className="lg:col-span-4">
          <div className="sticky top-28 overflow-hidden rounded-lg bg-foreground-950 text-background-50">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary-500 via-accent-500 to-primary-500" />
            <div className="flex flex-col p-5 md:p-6">
              <span className="eyebrow text-[11px] font-bold text-accent-400">DISCUSSION</span>
              <h2 className="mt-2 font-display text-2xl font-black leading-tight">
                评论区
                <span className="block text-background-50/80">吃瓜也讲证据</span>
              </h2>
              <div className="mt-4 flex items-center gap-4 text-sm text-background-50/85">
                <span className="flex items-center gap-1.5">
                  <i className="ri-fire-fill text-accent-400" /> 5.6万 参与
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-fill" /> 186万 浏览
                </span>
              </div>
              <p className="mt-4 text-[13px] leading-6 text-background-100/80">
                每条热评背后都有证据支撑才够硬。理性补充截图、时间线，拒绝空口带节奏。
              </p>

              <div className="mt-5 border-t border-dashed border-background-50/20 pt-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-500 font-display text-base font-black text-background-50">
                    曝
                  </span>
                  <input
                    type="text"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submit()}
                    placeholder="补充你的证据 / 看法…"
                    className="w-full rounded-md border border-background-50/20 bg-background-50/10 px-3 py-2 text-sm text-background-50 placeholder:text-background-50/40 focus:border-accent-400 focus:outline-none"
                  />
                </div>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 text-[11px] text-background-100/70">
                    <i className="ri-user-heart-line" />
                    文明发言 · 仅演示
                  </span>
                  <button
                    type="button"
                    onClick={submit}
                    className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-1.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    发布
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 热评列表 */}
        <div className="lg:col-span-8">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h2 className="flex items-center gap-2 font-display text-xl font-black text-foreground-950">
              <i className="ri-chat-heart-fill text-primary-500" />
              热评精选
            </h2>
            <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1 text-[12px]">
              {["最热", "最新", "理性派"].map((t, i) => (
                <button
                  key={t}
                  type="button"
                  className={`whitespace-nowrap rounded-full px-3 py-1 font-medium transition-colors ${
                    i === 0 ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-primary-600"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {[...(extra ? [extra] : []), ...hotComments].map((c, i) => {
              const real = c as (typeof hotComments)[number];
              const fake = !("id" in c);
              const id = fake ? "extra" : real.id;
              const agree = fake ? 1 : real.agree;
              const liked = agreed.includes(id);
              const name = fake ? c.name : real.name;
              const text = fake ? c.text : real.text;
              return (
                <div
                  key={id + String(i)}
                  className="rounded-lg border border-background-200 bg-background-100 p-4 transition-colors hover:bg-background-50"
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center font-display text-sm font-black text-foreground-950 ${
                        avatarColors[i % avatarColors.length]
                      } rounded-full`}
                    >
                      {name.slice(0, 1)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-bold text-foreground-950">{name}</span>
                        {!fake && (
                          <span
                            className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                              badgeStyle[real.badge] ?? "bg-background-200 text-foreground-600"
                            }`}
                          >
                            {real.badge}
                          </span>
                        )}
                        <span className="text-[11px] text-foreground-400">{fake ? "刚刚" : real.time}</span>
                      </div>
                      <p className="mt-1.5 text-sm leading-6 text-foreground-700">{text}</p>
                      <div className="mt-2.5 flex flex-wrap items-center gap-4 text-[11px] text-foreground-500">
                        <button
                          type="button"
                          onClick={() => toggle(id)}
                          className={`flex items-center gap-1 rounded-full px-2.5 py-1 font-medium transition-colors ${
                            liked ? "bg-primary-100 text-primary-700" : "hover:bg-background-200 hover:text-primary-600"
                          }`}
                        >
                          <i className={liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"} />
                          同感 {liked ? agree + 1 : agree}
                        </button>
                        <button type="button" className="flex items-center gap-1 hover:text-primary-600">
                          <i className="ri-share-forward-line" /> 转发
                        </button>
                        <button type="button" className="flex items-center gap-1 hover:text-primary-600">
                          <i className="ri-chat-1-line" /> 回复
                        </button>
                        <span className="ml-auto flex items-center gap-1 text-foreground-400">
                          <i className="ri-flag-line" /> 疑似带节奏？
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-5 flex justify-center">
            <a
              href="#comments"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-5 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
            >
              查看全部 {hotComments.length} 条热评 <i className="ri-arrow-down-line" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}